import { cva, type VariantProps } from 'class-variance-authority'
import { type ViewProps } from 'react-native'
import Animated, { type AnimatedProps } from 'react-native-reanimated'
import { cn } from '~/lib/utils'

const badgeVariants = cva(
  'web:inline-flex web:transition-colors web:focus:outline-none items-center rounded-full',
  {
    variants: {
      variant: {
        default: 'border-transparent bg-primary',
        primary: 'border-transparent bg-accent',
        secondary: 'border-transparent bg-secondary',
        destructive: 'border-transparent bg-destructive',
        outline: 'border border-secondary text-foreground',
        success: 'bg-success',
        warning: 'bg-warning',
      },
      size: {
        xxs: 'px-1.5 py-0.5',
        xs: 'px-2 py-1',
        sm: 'px-3 py-0.5',
        md: 'px-3 py-1.5',
        lg: 'px-4 py-2',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'sm',
    },
  }
)

const badgeTextVariants = cva('text-background', {
  variants: {
    variant: {
      default: 'text-tertiary',
      primary: 'text-accent-foreground',
      secondary: 'text-background',
      destructive: 'text-background',
      outline: 'text-primary',
      success: 'text-background',
      warning: 'text-background',
    },
    size: {
      xxs: 'text-xxs',
      xs: 'text-xs',
      sm: 'text-sm',
      md: 'text-base',
      lg: 'font-medium text-lg',
    },
  },
  defaultVariants: {
    variant: 'default',
    size: 'sm',
  },
})

type BadgeProps = AnimatedProps<ViewProps> &
  VariantProps<typeof badgeVariants> & { textClassName?: string }

function Badge({ className, variant, children, textClassName, size, ...props }: BadgeProps) {
  return (
    <Animated.View className={cn(badgeVariants({ variant, size }), className)} {...props}>
      {typeof children === 'string' ? (
        <Animated.Text className={cn(badgeTextVariants({ variant, size }), textClassName)}>
          {children}
        </Animated.Text>
      ) : (
        children
      )}
    </Animated.View>
  )
}

export { Badge, badgeTextVariants, badgeVariants }
export type { BadgeProps }
